import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField,
  Grid,
  Pagination,
  InputAdornment,
  MenuItem,
  Collapse,
  Tooltip,
} from "@mui/material";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import config from "../../config";


const PAGE_SIZE = 20;

const LEVEL_COLOR = {
  error: "error",
  warn: "warning",
  warning: "warning",
  info: "info",
  debug: "default",
};

const levelColor = (level) => LEVEL_COLOR[(level || "").toLowerCase()] || "default";

const getMetaFile = (metadata) => metadata?.filename || metadata?.file_name || "";

const getMetaSummary = (metadata) => {
  if (!metadata) return "";
  if (metadata.error) return metadata.error;
  const reasons = metadata.reason || metadata.reasons;
  if (Array.isArray(reasons)) return reasons.join(", ");
  if (typeof reasons === "string") return reasons;
  return "";
};

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

function MetadataPanel({ log }) {
  return (
    <Box
      sx={{
        background: "#F9FAFB",
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color: "#6B7280", display: "block", mb: 0.5 }}
      >
        FULL METADATA
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          fontSize: 12,
          fontFamily: "monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 320,
          overflow: "auto",
        }}
      >
        {JSON.stringify(log.metadata, null, 2)}
      </Box>
      <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block", mt: 1 }}>
        ID: {log._id}
        {log.endpoint ? ` · Endpoint: ${log.endpoint}` : ""}
        {log.ip ? ` · IP: ${log.ip}` : ""}
        {log.userId ? ` · User: ${log.userId}` : ""}
      </Typography>
    </Box>
  );
}

export default function LogsViewer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [levelFilter, serviceFilter]);

  const fetchLogs = useCallback(async () => {
    try {
      setRefreshing(true);
      setErrorMsg("");
      const token = sessionStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/logs/all`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.success) {
        const sorted = (Array.isArray(data.logData) ? data.logData : []).slice().sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setLogs(sorted);
      } else {
        setLogs([]);
        setErrorMsg(data.message || "Failed to load logs");
      }
    } catch (error) {
      setLogs([]);
      setErrorMsg("Something went wrong while loading logs");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const levelOptions = useMemo(() => {
    const set = new Set(logs.map((l) => (l.level || "").toLowerCase()).filter(Boolean));
    return Array.from(set);
  }, [logs]);

  const serviceOptions = useMemo(() => {
    const set = new Set(logs.map((l) => l.service).filter(Boolean));
    return Array.from(set);
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return logs.filter((log) => {
      if (levelFilter !== "all" && (log.level || "").toLowerCase() !== levelFilter) return false;
      if (serviceFilter !== "all" && log.service !== serviceFilter) return false;
      if (!term) return true;
      const haystack = [
        log.message,
        log.service,
        log.level,
        getMetaFile(log.metadata),
        getMetaSummary(log.metadata),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [logs, searchTerm, levelFilter, serviceFilter]);

  const total = filteredLogs.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const pagedLogs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredLogs.slice(start, start + PAGE_SIZE);
  }, [filteredLogs, page]);

  const clearFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setLevelFilter("all");
    setServiceFilter("all");
    setPage(1);
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            mb={4}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <Typography variant="h4" fontWeight={800}>
                Logs
              </Typography>

              {refreshing && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Stack>

            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={fetchLogs}
                  disabled={refreshing}
                  sx={{ border: "1px solid #E5E7EB", borderRadius: "12px" }}
                >
                  <RefreshRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Paper
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search message, file, service…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchInput("")}>
                          <ClearRoundedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Level"
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {levelOptions.map((lvl) => (
                    <MenuItem key={lvl} value={lvl}>
                      {lvl.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Service"
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {serviceOptions.map((svc) => (
                    <MenuItem key={svc} value={svc}>
                      {svc}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={clearFilters}
                  sx={{
                    height: 40,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {errorMsg && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Typography>
          )}

          <Box sx={{ position: "relative" }}>
            {refreshing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(248,250,252,0.6)",
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box
              sx={{
                opacity: refreshing ? 0.5 : 1,
                pointerEvents: refreshing ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
            >
              {isMobile ? (
                <Stack spacing={2}>
                  {pagedLogs.length > 0 ? (
                    pagedLogs.map((log) => {
                      const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;
                      const isExpanded = expandedId === log._id;
                      const file = getMetaFile(log.metadata);
                      const summary = getMetaSummary(log.metadata);

                      return (
                        <Paper
                          key={log._id}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Chip
                              label={(log.level || "—").toUpperCase()}
                              size="small"
                              color={levelColor(log.level)}
                            />
                            <Typography variant="caption" sx={{ color: "#6B7280" }}>
                              {formatTimestamp(log.createdAt)}
                            </Typography>
                          </Stack>

                          <Typography fontWeight={700} sx={{ mt: 1 }}>
                            {log.message || "—"}
                          </Typography>

                          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
                            {log.service || "—"}
                          </Typography>

                          {file && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {file}
                            </Typography>
                          )}

                          {summary && (
                            <Typography variant="body2" sx={{ color: "#6B7280" }}>
                              {summary}
                            </Typography>
                          )}

                          {hasMetadata && (
                            <>
                              <IconButton size="small" onClick={() => toggleExpand(log._id)} sx={{ mt: 1 }}>
                                {isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                              </IconButton>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ mt: 1 }}>
                                  <MetadataPanel log={log} />
                                </Box>
                              </Collapse>
                            </>
                          )}
                        </Paper>
                      );
                    })
                  ) : (
                    <Paper sx={{ p: 3, textAlign: "center" }}>
                      <Typography>No logs found</Typography>
                    </Paper>
                  )}
                </Stack>
              ) : (
                <Paper sx={{ borderRadius: 3 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }} />
                          <TableCell>Time</TableCell>
                          <TableCell>Level</TableCell>
                          <TableCell>Service</TableCell>
                          <TableCell>Message</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>Reason / Error</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {pagedLogs.length > 0 ? (
                          pagedLogs.map((log) => {
                            const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;
                            const file = getMetaFile(log.metadata);
                            const summary = getMetaSummary(log.metadata);
                            const isExpanded = expandedId === log._id;

                            return (
                              <React.Fragment key={log._id}>
                                <TableRow hover>
                                  <TableCell>
                                    {hasMetadata && (
                                      <IconButton size="small" onClick={() => toggleExpand(log._id)}>
                                        {isExpanded ? (
                                          <ExpandLessRoundedIcon fontSize="small" />
                                        ) : (
                                          <ExpandMoreRoundedIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                                    {formatTimestamp(log.createdAt)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={(log.level || "—").toUpperCase()}
                                      size="small"
                                      color={levelColor(log.level)}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ whiteSpace: "nowrap" }}>{log.service || "—"}</TableCell>
                                  <TableCell>{log.message || "—"}</TableCell>
                                  <TableCell
                                    sx={{
                                      maxWidth: 240,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={file}
                                  >
                                    {file || "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      maxWidth: 320,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={summary}
                                  >
                                    {summary || "—"}
                                  </TableCell>
                                </TableRow>

                                {hasMetadata && (
                                  <TableRow>
                                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}>
                                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ my: 1.5 }}>
                                          <MetadataPanel log={log} />
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No logs found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {total > 0 && totalPages > 1 && (
                <Stack alignItems="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    size={isMobile ? "small" : "medium"}
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                  />
                  <Typography variant="caption" sx={{ color: "#6B7280", mt: 1 }}>
                    Page {page} of {totalPages} · {total} total
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}